import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Input, Row, Checkbox, Button } from 'antd';
import { toast } from 'react-toastify';
import { Cards } from '../../components/cards/frame/cards-frame';
import Loader from '../../components/loader/Loader';
import RegistrationSuccessModal from '../../components/registration-success-modal/RegistrationSuccessModal';
// import { clearSourcePackageId } from '../../redux/merchantRegistration/actions';
import { submitMerchantRegistrationApi } from '../../redux/sagas/requests/merchantRegistrationRequest';
import { setTransparentLoader } from '../../redux/loader/loaderAction';
import { selectLanguage } from '../../redux/authentication/actions';
import Switch from '../../components/language-change-switch/Switch';
import { InputNumber } from '../../components/InputNumber';
import {
  PEC_REGEX_PATTERN,
  ATECO_REGEX_PATTERN,
  VAT_REGEX_PATTERN,
  CF_REGEX_PATTERN,
} from '../../utility/validationConstants';

/** API (snake_case) field name -> Form (camelCase) field name for form.setFields */
const API_TO_FORM_FIELD_MAP = {
  first_name: 'firstName',
  last_name: 'lastName',
  company_name: 'companyName',
  email: 'email',
  mobile_number: 'mobile',
  vat_number: 'vatNumber',
  ateco_code: 'atecoCode',
  intended_product_services: 'products',
  annual_revenue: 'revenue',
  prohibited_products_accepted: 'confirmProhibited',
  // source_package_id has no form field (comes from URL/state)
};

// const MAX_LENGTH_NAME = 30;
const MAX_COMPANY_NAME_LENGTH = 30;
const EMAIL_LENGTH = 100;
// const MOBILE_NUMBER_LENGTH = 15;
const VAT_NUMBER_LENGTH = 11;
const ATECO_CODE_LENGTH = 8;
const INTENDED_PRODUCTS_MAX_LENGTH = 100;
const ANNUAL_REVENUE_MAX_LENGTH = 20;

/** Italian Codice Fiscale: exactly 16 chars - 6 letters, 2 digits, 1 letter, 2 digits, 1 letter, 3 digits, 1 letter */
// const CF_CODE_LENGTH = 16;
// const CF_CODE_REGEX = /^[A-Za-z]{6}[0-9]{2}[A-Za-z][0-9]{2}[A-Za-z][0-9]{3}[A-Za-z]$/;
// const CF_CODE_VALIDATION_MESSAGE =
//   'CF Code must be a valid Italian Codice Fiscale (16 characters: 6 letters, 2 digits, 1 letter, 2 digits, 1 letter, 3 digits, 1 letter).';

function getSwitchValueFromStoredLang() {
  const storedLang = localStorage.getItem('setLang');
  return storedLang === 'it' || storedLang === 'ita' ? 'IT' : 'EN';
}

function MerchantRegistration() {
  const dispatch = useDispatch();
  const language = useSelector((state) => state?.auth?.language);
  const sourcePackegeId = useSelector((state) => state?.merchantRegistration?.sourcePackageId);
  const transparentLoader = useSelector((state) => state?.loader?.transparentLoader);
  const [form] = Form.useForm();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getSwitchValueFromStoredLang);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [apiFormErrors, setApiFormErrors] = useState(null);
  const errorAlertRef = useRef(null);
  // const canClearOnUnmountRef = useRef(false);

  const scrollToErrorTop = () => {
    setTimeout(() => {
      const el = errorAlertRef.current;
      if (el) {
        const headerOffset = 120;
        const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }, 400);
  };

  const applyFormErrorsFromApi = (formErrorsObj) => {
    if (!formErrorsObj || typeof formErrorsObj !== 'object') return;
    // setApiFormErrors(formErrorsObj);
    const setFieldsArgs = [];
    Object.entries(formErrorsObj).forEach(([apiKey, message]) => {
      const formFieldName = API_TO_FORM_FIELD_MAP[apiKey];
      const msg = Array.isArray(message) ? message[0] : message;
      if (formFieldName && msg) {
        setFieldsArgs.push({ name: formFieldName, errors: [String(msg)] });
      }
    });
    if (setFieldsArgs.length > 0) {
      form.setFields(setFieldsArgs);
    }
  };

  const handleLanguageChange = (val) => {
    setSelectedLanguage(val);
    dispatch(selectLanguage(val === 'EN' ? 'en' : 'ita'));
  };

  const onFinish = async (values) => {
    const payload = {
      source_package_id: String(sourcePackegeId ?? ''),
      cf_code: (values.cf_code?.trim() ?? '').toUpperCase(),
      company_name: values.companyName?.trim() ?? '',
      email: values.email?.trim() ?? '',
      vat_number: values.vatNumber?.trim() ?? '',
      ateco_code: values.atecoCode?.trim(),
      intended_product_services: values.products?.trim() ?? '',
      annual_revenue: values.revenue != null ? String(values.revenue) : '',
      prohibited_products_accepted: Boolean(values.confirmProhibited),
    };
    dispatch(setTransparentLoader(true));
    setIsSubmitting(true);
    try {
      const { data } = await submitMerchantRegistrationApi(payload);
      if (data?.status) {
        // setApiFormErrors(null);
        setShowSuccessModal(true);
        form.resetFields();
        toast.success(
          data?.message ||
            language?.content?.REGISTRATION_SUBMITTED_SUCCESS ||
            'Merchant registration submitted successfully',
        );
      } else {
        const formErrors = data?.errors?.form_errors || data?.form_errors || {};
        // applyFormErrorsFromApi(formErrors);
        // scrollToErrorTop();
        if (Object.keys(formErrors).length > 0) {
          applyFormErrorsFromApi(formErrors);
          scrollToErrorTop();
        } else {
          const message =
            data?.errors?.message ||
            language?.content?.FAILED_SUBMIT_REGISTRATION ||
            'Failed to submit merchant registration';
          toast.error(message);
        }
      }
    } catch (error) {
      const message =
        error?.message || language?.content?.FAILED_SUBMIT_REGISTRATION || 'Failed to submit merchant registration';
      toast.error(message);
    } finally {
      dispatch(setTransparentLoader(false));
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    // setApiFormErrors(null);
  };

  // Default to English when no language is stored (merchant registration first visit)
  useEffect(() => {
    if (!localStorage.getItem('setLang')) {
      dispatch(selectLanguage('en'));
    }
  }, [dispatch]);

  // Stop transparent loader when user lands on this screen
  useEffect(() => {
    dispatch(setTransparentLoader(false));
  }, [dispatch]);
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     canClearOnUnmountRef.current = true;
  //   }, 300);
  //   dispatch(setTransparentLoader(false));
  //   return () => {
  //     clearTimeout(timer);
  //     if (canClearOnUnmountRef.current) {
  //       dispatch(clearSourcePackageId());
  //     }
  //   };
  // }, [dispatch]);

  // Re-validate fields with errors when language changes so validation messages update
  useEffect(() => {
    const fieldsError = form.getFieldsError();
    const fieldsWithErrors = fieldsError.filter(({ errors }) => errors?.length > 0).map(({ name }) => name);
    if (fieldsWithErrors.length > 0) {
      form.validateFields(fieldsWithErrors).catch(() => {
        // Ignore validation failures; we only want to refresh error messages in the new language
      });
    }
  }, [language, form]);

  return (
    <>
      {transparentLoader && <Loader />}
      <Row gutter={32} className="merchant-onboarding-form-row" style={{ marginTop: '80px' }}>
        <Col sm={24} xs={24} style={{ marginBottom: '80px' }}>
          <Cards
            title={language?.content?.MERCHANT_REGISTRATION || 'Merchant Registration'}
            titleStyle={{ fontSize: '21px', fontWeight: '600' }}
            titleDescriptionStyle={{ fontSize: '16px', fontWeight: '400' }}
            titleDescription={
              language?.content?.MERCHANT_REGISTRATION_INTRO ||
              'Please complete the form below to register as a merchant on Tripago.'
            }
            bodypadding="32px"
            extra={<Switch value={selectedLanguage} onChange={handleLanguageChange} />}
            // titlePadding="32px 0px"
          >
            <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
              <Row gutter={32}>
                {/* 3. Company Name */}
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="companyName"
                    label={language?.content?.COMPANY_NAME || 'Company Name'}
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                      {
                        validator: (_, value) => {
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(language?.content?.COMPANY_NAME_REQUIRED || 'Company name is required'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        max: MAX_COMPANY_NAME_LENGTH,
                        message: language?.content?.COMPANY_NAME_MAX || 'Company name must not exceed 100 characters',
                      },
                    ]}
                  >
                    <Input
                      placeholder={language?.content?.ENTER_COMPANY_NAME || 'Enter Company Name'}
                      maxLength={MAX_COMPANY_NAME_LENGTH}
                    />
                  </Form.Item>
                </Col>

                {/* 4. Email Address */}
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="email"
                    label={language?.content?.EMAIL_ADDRESS || 'Email Address'}
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                      {
                        validator: (_, value) => {
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(language?.content?.EMAIL_ADDRESS_REQUIRED || 'Please enter email'),
                            );
                          }
                          if (!PEC_REGEX_PATTERN.test(String(value).trim())) {
                            return Promise.reject(
                              new Error(language?.content?.EMAIL_ADDRESS_NOT_VALID || 'Email Address is not valid.'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        max: EMAIL_LENGTH,
                        message: language?.content?.EMAIL_ADDRESS_MAX || 'Email address must not exceed 100 characters',
                      },
                    ]}
                  >
                    <Input
                      placeholder={language?.content?.ENTER_EMAIL_ADDRESS || 'Enter Email Address'}
                      maxLength={EMAIL_LENGTH}
                    />
                  </Form.Item>
                </Col>

                {/* 6. VAT Number */}
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="vatNumber"
                    label={language?.content?.VAT_NUMBER || 'VAT Number'}
                    rules={[
                      { required: true, message: '' },
                      {
                        validator: (_, value) => {
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(language?.content?.VAT_NUMBER_REQUIRED || 'VAT Number is required'),
                            );
                          }
                          const trimmed = String(value).trim().replace(/\D/g, '');
                          if (!VAT_REGEX_PATTERN.test(trimmed)) {
                            return Promise.reject(
                              new Error(language?.content?.VAT_NUMBER_NOT_VALID || 'VAT Number is not valid.'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder={language?.content?.PLACEHOLDER_VAT_NUMBER || '12345678901'}
                      maxLength={VAT_NUMBER_LENGTH}
                      decimalPlaces={0}
                    />
                  </Form.Item>
                </Col>

                {/* 7. ATECO Code - format XX.XX.XX (e.g. 62.02.25) */}
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="atecoCode"
                    label={language?.content?.ATECO_CODE || 'ATECO Code'}
                    rules={[
                      { required: true, message: '' },
                      {
                        validator: (_, value) => {
                          if (value == null || String(value).trim() === '') {
                            return Promise.reject(
                              new Error(language?.content?.ATECO_CODE_REQUIRED || 'ATECO Code is required'),
                            );
                          }
                          const trimmed = String(value).trim();
                          if (!ATECO_REGEX_PATTERN.test(trimmed)) {
                            return Promise.reject(
                              new Error(language?.content?.ATECO_CODE_NOT_VALID || 'ATECO Code is not valid.'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={language?.content?.PLACEHOLDER_ATECO_CODE || '62.02.25'}
                      maxLength={ATECO_CODE_LENGTH}
                      // onChange={(e) => {
                      //   const raw = e.target.value.replace(/[^0-9.]/g, '');
                      //   form.setFieldValue('atecoCode', raw);
                      // }}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        let formatted = digits;
                        if (digits.length > 2) formatted = `${digits.slice(0, 2)}.${digits.slice(2)}`;
                        if (digits.length > 4)
                          formatted = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
                        form.setFieldValue('atecoCode', formatted);
                      }}
                    />
                  </Form.Item>
                </Col>

                {/* 8. Intended Products/Services */}
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="products"
                    label={language?.content?.INTENDED_PRODUCTS_OR_SERVICES || 'Intended Products or Services to Sell'}
                    rules={[
                      { required: true, message: '' },
                      {
                        validator: (_, value) => {
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(
                                language?.content?.INTENDED_PRODUCTS_REQUIRED ||
                                  'Intended Products or Services to Sell is required',
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        max: INTENDED_PRODUCTS_MAX_LENGTH,
                        message:
                          language?.content?.INTENDED_PRODUCTS_MAX ||
                          `Intended products/services must not exceed ${INTENDED_PRODUCTS_MAX_LENGTH} characters`,
                      },
                    ]}
                  >
                    <Input
                      placeholder={
                        language?.content?.ENTER_INTENDED_PRODUCTS_SERVICES || 'Enter Intended Products/Services'
                      }
                      maxLength={INTENDED_PRODUCTS_MAX_LENGTH}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={12} xl={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="cf_code"
                    label={language?.content?.CF_CODE || 'CF Code'}
                    rules={[
                      { required: true, message: '' },
                      {
                        validator: (_, value) => {
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(language?.content?.CF_CODE_REQUIRED || 'CF Code is required'),
                            );
                          }
                          const trimmed = String(value).trim().toUpperCase();
                          if (!CF_REGEX_PATTERN.test(trimmed)) {
                            return Promise.reject(
                              new Error(language?.content?.CF_CODE_NOT_VALID || 'CF Code is not valid.'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={language?.content?.PLACEHOLDER_CF_CODE || 'RSSMRA85M01H501Z'}
                      maxLength={16}
                      onChange={(e) => {
                        const upper = e.target.value
                          .replace(/[^A-Za-z0-9]/g, '')
                          .toUpperCase()
                          .slice(0, 16);
                        form.setFieldValue('cf_code', upper);
                      }}
                    />
                  </Form.Item>
                </Col>

                {/* 9. Annual Revenue */}
                <Col span={24}>
                  <Form.Item
                    name="revenue"
                    label={language?.content?.ANNUAL_REVENUE || 'Annual Revenue'}
                    rules={[
                      {
                        required: true,
                        message: language?.content?.ANNUAL_REVENUE_REQUIRED || 'Annual Revenue is required',
                      },

                      {
                        validator: (_, value) => {
                          if (value == null || value === '') {
                            return Promise.resolve(); // required rule handles empty
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      className="input-prefix-currency"
                      decimalPlaces={2}
                      style={{ width: '100%', height: '40px', paddingLeft: '0 !important' }}
                      placeholder={language?.content?.ENTER_ANNUAL_REVENUE || 'Enter Annual Revenue'}
                      min={0}
                      prefix={language?.content?.CURRENCY || '€'}
                      maxLength={ANNUAL_REVENUE_MAX_LENGTH}
                    />
                  </Form.Item>
                </Col>

                {/* Prohibited Products Checkbox */}
                <Col span={24}>
                  <Form.Item
                    className="confirm-prohibited-form-item"
                    name="confirmProhibited"
                    valuePropName="checked"
                    style={{ minHeight: '30px !important' }}
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  language?.content?.MUST_CONFIRM_TO_PROCEED ||
                                    'You must confirm that prohibited products will not be sold.',
                                ),
                              ),
                      },
                    ]}
                  >
                    <Checkbox>
                      {language?.content?.CONFIRM_PROHIBITED_PRODUCTS ||
                        'I confirm that prohibited products will not be sold'}
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              {/* Footer Buttons */}
              <div
                className="merchant-registration-footer-buttons"
                // style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}
              >
                <Button className="cancel-button" onClick={handleReset} disabled={isSubmitting}>
                  {language?.content?.CANCEL || 'Cancel'}
                </Button>
                <Button className="submit-button" type="primary" htmlType="submit" loading={isSubmitting}>
                  {language?.content?.SUBMIT_REGISTRATION || 'Submit Registration'}
                </Button>
              </div>
            </Form>
          </Cards>
        </Col>
      </Row>

      <RegistrationSuccessModal
        width={500}
        visible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={language?.content?.REGISTRATION_THANK_YOU || 'Thank you for Registration!'}
        messages={
          <div>
            <div>{language?.content?.REGISTRATION_UNDER_REVIEW || 'Your request is currently under review.'}</div>
            <div>
              {language?.content?.REGISTRATION_APPROVAL_NOTIFICATION ||
                'You will receive an email notification once your application has been approved or rejected.'}
            </div>
          </div>
        }
      />
    </>
  );
}

export default React.memo(MerchantRegistration);
